import {
    Box,
    Button,
    Container,
    FormControl,
    FormHelperText,
    FormLabel,
    Input,
    Text,
    useToast,
} from '@chakra-ui/react';
import Colors from '../../../config/colors';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    useEditUserMutation,
    useLazyGetUserQuery,
} from '../../../redux/platimarket/platimarket.api';
import Loader from '../../loader/Loader';
import { checkIsValidURL, parseSteamIdFromURL } from '../../../utils/steam';

import { FiExternalLink } from 'react-icons/fi';
import { Link } from 'react-router-dom';

export default function SettingsPage() {
    const [t] = useTranslation();
    const toast = useToast();

    const [getUser, { data: user, isFetching: isLoadingGetUser, isSuccess: isSuccessGetUser }] =
        useLazyGetUserQuery();

    const [
        editUser,
        {
            isLoading: isLoadingEditUser,
            data: editedUser,
            isSuccess: isSuccessEditUser,
            isError: isErrorEditUser,
        },
    ] = useEditUserMutation();

    const [steamProfileURL, setSteamProfileURL] = useState<string>('');
    const currentSteamId = parseSteamIdFromURL(steamProfileURL);

    const [errorMessage, setErrorMessage] = useState('');

    const isCanToSave =
        !!steamProfileURL &&
        !errorMessage.length &&
        currentSteamId !== parseSteamIdFromURL(user?.steamProfileURL ?? '') &&
        currentSteamId !== parseSteamIdFromURL(editedUser?.steamProfileURL ?? '');

    const onEditUser = () => {
        if (steamProfileURL) {
            editUser({ steamProfileURL });
        }
    };

    useEffect(() => {
        setErrorMessage('');
        if (steamProfileURL.length) {
            const isValidURL = checkIsValidURL(steamProfileURL) && currentSteamId;

            if (!isValidURL) {
                setErrorMessage(t('please-enter-the-correct-link'));
            }
        }
    }, [steamProfileURL]);

    useEffect(() => {
        getUser();
    }, []);

    useEffect(() => {
        if (isSuccessEditUser && editedUser) {
            localStorage.setItem('steamProfileURL', steamProfileURL);

            toast({
                description: t('link-successfully-changed'),
                status: 'success',
                duration: 2000,
            });
        }
    }, [isSuccessEditUser]);

    useEffect(() => {
        if (isErrorEditUser) {
            toast({
                description: t('an-error-occurred'),
                status: 'error',
                duration: 2000,
            });
        }
    }, [isErrorEditUser]);

    useEffect(() => {
        if (isSuccessGetUser && user) {
            setSteamProfileURL(user.steamProfileURL ?? '');
        }
    }, [isLoadingGetUser]);

    return (
        <Container>
            <Box display="flex" flexDirection="column" gap="3">
                <FormControl color="#fff" isInvalid={!!errorMessage.length}>
                    <FormLabel>{t('link-to-you-steam-account')}</FormLabel>
                    <Box display="flex" alignItems="center" gap="2">
                        <Input
                            placeholder={t('enter-the-link-to-your-steam-account')}
                            type="text"
                            border="none"
                            background={Colors.darkBlue2}
                            color={Colors.lightGrey2}
                            value={steamProfileURL ?? ''}
                            onChange={(e) => setSteamProfileURL(e.target.value)}
                        />
                        {isLoadingGetUser && (
                            <Box display="flex" justifyContent="center">
                                <Loader width="20px" height="20px" thickness="3px" />
                            </Box>
                        )}
                    </Box>
                    {!!errorMessage.length && (
                        <FormHelperText color={Colors.red} fontWeight="300">
                            {errorMessage}
                        </FormHelperText>
                    )}
                </FormControl>
                <Text fontSize="14px" color={Colors.lightGrey2} fontWeight="300">
                    {t('steam-link-description')}
                </Text>
                <Text
                    as={Link}
                    to={process.env.REACT_APP_INSTRUCTIONS_FOR_GETTING_A_PROFILE_LINK_URL}
                    target="_blank"
                    display="flex"
                    alignItems="center"
                    gap="2"
                    color={Colors.lightBlue}
                    fontWeight="300"
                    fontSize="14px"
                    mt="10px">
                    <FiExternalLink />
                    {t('find-out-the-profile-link')}
                </Text>
                <Box
                    position="fixed"
                    display="flex"
                    alignItems="center"
                    bottom="0"
                    zIndex="3"
                    left="0"
                    right="0">
                    <Button
                        onClick={onEditUser}
                        isDisabled={!isCanToSave}
                        color="white"
                        background={Colors.blue}
                        fontSize="16px"
                        fontWeight="500"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        gap="12px"
                        width="100%"
                        padding="30px 0px"
                        borderRadius="0px"
                        backgroundImage={
                            process.env.PUBLIC_URL + '/images/categories/background_spotlight.jpg'
                        }
                        backgroundSize="cover"
                        backgroundPosition="bottom"
                        className={isCanToSave ? 'shine' : ''}>
                        <Text>{t('save')}</Text>
                    </Button>
                </Box>
            </Box>
        </Container>
    );
}
